/**
 * Dashboard — el resumen del día, distinto para cada rol.
 *
 * Antes era una maqueta: cuatro tarjetas con "—" fijo y una lista que
 * anunciaba como "próximamente" módulos que ya funcionaban. Es la primera
 * pantalla que ven los cinco roles, así que decía justo lo contrario de la
 * verdad.
 *
 * Cada bloque se consulta SOLO si el usuario tiene el permiso que lo respalda.
 * Sin eso, un contable pidiendo /citas cosecharía un 403 en su propia portada.
 * Todos los datos salen de endpoints que ya existían.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  FileText,
  Stethoscope,
  Wallet,
} from "lucide-react";
import { type Cita, listCitas } from "../api/citas";
import { listPagos } from "../api/pagos";
import { useAuth } from "../auth/AuthProvider";
import { useZonaHoraria, useBase } from "../auth/TenantBrandingProvider";
import { diaEnZona, hora, hoyEnZona } from "../lib/fecha";
import { Badge, Card, CardBody, CardHeader } from "../components/ui";

export function DashboardPage() {
  const { me } = useAuth();
  const base = useBase();
  const tz = useZonaHoraria();
  const hoy = hoyEnZona(tz);

  const puede = (c: string) => me?.is_super_admin || (me?.permisos.includes(c) ?? false);
  // Por permiso y no por código de rol: quien no puede ver a todos los
  // pacientes tampoco debe ver la agenda entera, y así un rol a medida se
  // comporta solo, sin tocar esta pantalla.
  const soloMias = !puede("paciente:read_all");

  // Un profesional ve SUS citas; recepción y admin, las del policlínico.
  const { data: citasHoy = [], isLoading: cargandoCitas } = useQuery({
    queryKey: ["citas", "dashboard", hoy, soloMias],
    queryFn: () => listCitas({ fecha: hoy, mias: soloMias }),
    enabled: puede("paciente:read"),
  });

  const { data: porCobrar = [] } = useQuery({
    queryKey: ["citas", "por_cobrar"],
    queryFn: () => listCitas({ por_cobrar: true }),
    enabled: puede("pago:create"),
  });

  const { data: pagos = [] } = useQuery({
    queryKey: ["pagos"],
    queryFn: () => listPagos(),
    enabled: puede("pago:read"),
  });

  const resumen = useMemo(() => {
    const activas = citasHoy.filter((c) => c.estado !== "CANCELADA");
    return {
      total: activas.length,
      porConfirmar: activas.filter((c) => c.estado === "PROGRAMADA").length,
      enAtencion: activas.filter((c) => c.estado === "EN_ATENCION").length,
      atendidas: activas.filter((c) => c.estado === "COMPLETADA").length,
      siguiente: activas
        .filter((c) => c.estado === "PROGRAMADA" || c.estado === "CONFIRMADA")
        .sort((a, b) => a.inicio.localeCompare(b.inicio))[0] as Cita | undefined,
    };
  }, [citasHoy]);

  const cobradoHoy = pagos
    .filter((p) => p.estado === "PAGADO" && diaEnZona(p.created_at, tz) === hoy)
    .reduce((s, p) => s + Number(p.monto), 0);
  const porValidar = pagos.filter((p) => p.estado === "PENDIENTE_VALIDACION").length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Hola, {me?.nombre_para_mostrar}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {me?.is_super_admin
            ? "Estás conectado como super-administrador: ves todos los policlínicos."
            : soloMias
              ? "Este es tu día."
              : "Resumen del día del policlínico."}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {puede("paciente:read") && (
          <>
            <Tarjeta
              icono={<CalendarClock className="h-5 w-5" />}
              color="bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)]"
              etiqueta={soloMias ? "Tus citas hoy" : "Citas hoy"}
              valor={cargandoCitas ? "…" : String(resumen.total)}
              a={`${base}/agenda`}
            />
            <Tarjeta
              icono={<CheckCircle2 className="h-5 w-5" />}
              color="bg-blue-50 text-blue-700"
              etiqueta="Atendidas"
              valor={cargandoCitas ? "…" : `${resumen.atendidas} de ${resumen.total}`}
            />
          </>
        )}
        {puede("pago:read") && (
          <Tarjeta
            icono={<Wallet className="h-5 w-5" />}
            color="bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)]"
            etiqueta="Cobrado hoy"
            valor={`S/ ${cobradoHoy.toFixed(2)}`}
            a={`${base}/pagos`}
          />
        )}
        {puede("pago:create") && (
          <Tarjeta
            icono={<Wallet className="h-5 w-5" />}
            color={porCobrar.length ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}
            etiqueta="Por cobrar"
            valor={String(porCobrar.length)}
            a={`${base}/caja`}
          />
        )}
        {puede("comprobante:read") && !puede("paciente:read") && (
          <Tarjeta
            icono={<FileText className="h-5 w-5" />}
            color="bg-slate-100 text-slate-600"
            etiqueta="Facturación"
            valor="Ver"
            a={`${base}/facturacion`}
          />
        )}
      </div>

      {/* Avisos que piden acción, no adorno */}
      {puede("pago:validar") && porValidar > 0 && (
        <Link
          to={`${base}/pagos`}
          className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 transition hover:bg-amber-100"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            <strong>{porValidar}</strong> constancia(s) de pago esperando validación. Hasta
            que se validen, esas citas siguen contando como no cobradas.
          </span>
        </Link>
      )}

      {puede("paciente:read") && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-semibold text-slate-900">
                <Stethoscope className="h-4 w-4 text-[color:var(--color-link)]" aria-hidden="true" />
                {soloMias ? "Tu agenda de hoy" : "Agenda de hoy"}
              </h2>
              <Link
                to={`${base}/agenda`}
                className="text-sm font-medium text-[color:var(--color-link)] hover:text-[color:var(--color-link)]"
              >
                Ver agenda completa
              </Link>
            </div>
            {resumen.siguiente && (
              <p className="mt-1 text-xs text-slate-500">
                Siguiente: {hora(resumen.siguiente.inicio, tz)} ·{" "}
                {resumen.siguiente.paciente_nombre}
              </p>
            )}
          </CardHeader>
          <CardBody className="p-0">
            {cargandoCitas && (
              <p className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</p>
            )}
            {!cargandoCitas && citasHoy.length === 0 && (
              <p className="px-6 py-8 text-center text-sm text-slate-500">
                No hay citas para hoy.
              </p>
            )}
            <ul className="divide-y divide-slate-100">
              {citasHoy
                .filter((c) => c.estado !== "CANCELADA")
                .sort((a, b) => a.inicio.localeCompare(b.inicio))
                .slice(0, 8)
                .map((c) => (
                  <li key={c.id} className="flex items-center gap-4 px-6 py-2.5">
                    <span className="w-14 shrink-0 font-mono text-sm text-slate-700">
                      {hora(c.inicio, tz)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-800">
                      {c.paciente_nombre}
                      <span className="ml-2 text-xs text-slate-400">
                        {c.especialidad_nombre}
                        {!soloMias && ` · ${c.profesional_nombre}`}
                      </span>
                    </span>
                    <EstadoCitaBadge estado={c.estado} />
                  </li>
                ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {!puede("paciente:read") && !puede("pago:read") && (
        <Card>
          <CardBody>
            <p className="py-6 text-center text-sm text-slate-500">
              Tu rol no incluye acceso a la agenda ni a los cobros. Usa el menú lateral
              para ir a tus secciones.
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

const ESTADO: Record<string, { label: string; variant: "neutral" | "success" | "warning" | "danger" }> = {
  PROGRAMADA: { label: "Programada", variant: "neutral" },
  CONFIRMADA: { label: "Confirmada", variant: "success" },
  EN_ATENCION: { label: "En atención", variant: "warning" },
  COMPLETADA: { label: "Atendida", variant: "success" },
  NO_ASISTIO: { label: "No asistió", variant: "danger" },
};

function EstadoCitaBadge({ estado }: { estado: string }) {
  const e = ESTADO[estado] ?? { label: estado, variant: "neutral" as const };
  return <Badge variant={e.variant}>{e.label}</Badge>;
}

function Tarjeta({
  icono,
  color,
  etiqueta,
  valor,
  a,
}: {
  icono: React.ReactNode;
  color: string;
  etiqueta: string;
  valor: string;
  a?: string;
}) {
  const contenido = (
    <CardBody className="flex items-start gap-3">
      <div className={`rounded-lg p-2 ${color}`}>{icono}</div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{etiqueta}</p>
        <p className="mt-1 text-2xl font-bold text-slate-900">{valor}</p>
      </div>
    </CardBody>
  );
  if (!a) return <Card>{contenido}</Card>;
  return (
    <Link to={a} className="block transition hover:opacity-90">
      <Card>{contenido}</Card>
    </Link>
  );
}
